/**
 * Organization model, legal entity scopes and group quota (plan 4.1–4.4).
 */
const ERRORS_EN = {
  NAME_REQUIRED: "Name is required.",
  CODE_INVALID: "Code: 1–20 letters, digits, dot, dash or underscore.",
  CODE_DUPLICATE: "This code is already used.",
  COUNTRY_INVALID: "Country must be a two-letter ISO code (e.g. TR, DE).",
  CURRENCY_INVALID: "Functional currency must be a three-letter ISO code (e.g. TRY, EUR).",
  FISCAL_YEAR_INVALID: "Fiscal year start month must be between 1 and 12.",
  LANGUAGE_INVALID: "Unsupported language.",
  TIMEZONE_INVALID: "Unknown time zone (use e.g. Europe/Istanbul).",
  TAX_ID_TYPE_INVALID: "Unknown tax ID type.",
  TAX_ID_DUPLICATE: "The same tax ID is entered twice.",
  ADDRESS_REQUIRED: "Legal address (street and city) is required.",
  LEGAL_ENTITY_REQUIRED: "Select a legal entity.",
  PRIMARY_INACTIVE: "The primary legal entity cannot be deactivated.",
  SCOPE_LEGAL_ENTITY_REQUIRED: "Select at least one legal entity or allow all.",
  SCOPE_UNKNOWN_LEGAL_ENTITY: "A selected legal entity no longer exists.",
  SCOPE_BRANCH_OUTSIDE: "A selected branch belongs to a legal entity that is not selected.",
  SCOPE_WAREHOUSE_OUTSIDE: "A selected warehouse belongs to a legal entity that is not selected.",
  QUOTA_USERS: "The group's user quota is full ({{users}} / {{limit}}). Add a license to raise the capacity."
};

const ERRORS_TR = {
  NAME_REQUIRED: "Ad zorunludur.",
  CODE_INVALID: "Kod: 1–20 harf, rakam, nokta, tire veya alt çizgi.",
  CODE_DUPLICATE: "Bu kod zaten kullanılıyor.",
  COUNTRY_INVALID: "Ülke iki harfli ISO kodu olmalı (ör. TR, DE).",
  CURRENCY_INVALID: "Fonksiyonel para birimi üç harfli ISO kodu olmalı (ör. TRY, EUR).",
  FISCAL_YEAR_INVALID: "Mali yıl başlangıç ayı 1 ile 12 arasında olmalı.",
  LANGUAGE_INVALID: "Desteklenmeyen dil.",
  TIMEZONE_INVALID: "Bilinmeyen saat dilimi (ör. Europe/Istanbul).",
  TAX_ID_TYPE_INVALID: "Bilinmeyen vergi kimliği türü.",
  TAX_ID_DUPLICATE: "Aynı vergi kimliği iki kez girilmiş.",
  ADDRESS_REQUIRED: "Yasal adres (sokak ve şehir) zorunludur.",
  LEGAL_ENTITY_REQUIRED: "Bir tüzel kişilik seçin.",
  PRIMARY_INACTIVE: "Birincil tüzel kişilik pasife alınamaz.",
  SCOPE_LEGAL_ENTITY_REQUIRED: "En az bir tüzel kişilik seçin ya da tümüne izin verin.",
  SCOPE_UNKNOWN_LEGAL_ENTITY: "Seçilen bir tüzel kişilik artık yok.",
  SCOPE_BRANCH_OUTSIDE: "Seçilen bir şube, seçilmemiş bir tüzel kişiliğe ait.",
  SCOPE_WAREHOUSE_OUTSIDE: "Seçilen bir depo, seçilmemiş bir tüzel kişiliğe ait.",
  QUOTA_USERS: "Grubun kullanıcı kotası dolu ({{users}} / {{limit}}). Kapasiteyi artırmak için lisans ekleyin."
};

const ERRORS_AR = {
  NAME_REQUIRED: "الاسم مطلوب.",
  CODE_INVALID: "الرمز: من 1 إلى 20 حرفًا أو رقمًا أو نقطة أو شرطة أو شرطة سفلية.",
  CODE_DUPLICATE: "هذا الرمز مستخدم بالفعل.",
  COUNTRY_INVALID: "يجب أن تكون الدولة رمز ISO من حرفين (مثل TR، DE).",
  CURRENCY_INVALID: "يجب أن تكون العملة الوظيفية رمز ISO من ثلاثة أحرف (مثل TRY، EUR).",
  FISCAL_YEAR_INVALID: "يجب أن يكون شهر بداية السنة المالية بين 1 و12.",
  LANGUAGE_INVALID: "لغة غير مدعومة.",
  TIMEZONE_INVALID: "منطقة زمنية غير معروفة (مثل Europe/Istanbul).",
  TAX_ID_TYPE_INVALID: "نوع رقم ضريبي غير معروف.",
  TAX_ID_DUPLICATE: "تم إدخال نفس الرقم الضريبي مرتين.",
  ADDRESS_REQUIRED: "العنوان القانوني (الشارع والمدينة) مطلوب.",
  LEGAL_ENTITY_REQUIRED: "اختر كيانًا قانونيًا.",
  PRIMARY_INACTIVE: "لا يمكن إلغاء تفعيل الكيان القانوني الرئيسي.",
  SCOPE_LEGAL_ENTITY_REQUIRED: "اختر كيانًا قانونيًا واحدًا على الأقل أو اسمح بالكل.",
  SCOPE_UNKNOWN_LEGAL_ENTITY: "أحد الكيانات القانونية المحددة لم يعد موجودًا.",
  SCOPE_BRANCH_OUTSIDE: "أحد الفروع المحددة يتبع كيانًا قانونيًا غير محدد.",
  SCOPE_WAREHOUSE_OUTSIDE: "أحد المستودعات المحددة يتبع كيانًا قانونيًا غير محدد.",
  QUOTA_USERS: "حصة المستخدمين للمجموعة ممتلئة ({{users}} / {{limit}}). أضف ترخيصًا لزيادة السعة."
};

export const organizationLocales = {
  en: {
    nav: { organization: "Organization" },
    org: {
      title: "Organization",
      subtitle: "Legal entities, branches and who can work in which of them.",
      loadError: "The organization could not be loaded.",
      saveError: "Could not save.",
      saved: "Saved.",
      readOnly: "Only the company account can change the organization.",
      tabs: { legalEntities: "Legal entities", branches: "Branches", scopes: "User access" },
      active: {
        label: "Working in",
        hint: "New records are booked to this legal entity."
      },
      fields: {
        code: "Code",
        name: "Name",
        country: "Country (ISO)",
        currency: "Functional currency",
        fiscalYearStart: "Fiscal year starts in",
        fiscalYear: "Current fiscal year",
        taxIds: "Tax IDs",
        taxType: "Type",
        taxValue: "Number",
        taxCountry: "Country",
        legalAddress: "Legal address",
        line1: "Street",
        line2: "Address line 2",
        city: "City",
        region: "Region / state",
        postalCode: "Postal code",
        language: "Language",
        timezone: "Time zone",
        status: "Status",
        legalEntity: "Legal entity",
        branch: "Branch",
        warehouses: "Warehouses"
      },
      statuses: { active: "Active", inactive: "Inactive" },
      taxTypes: { vkn: "VKN (TR)", tckn: "TCKN (TR)", vat: "VAT", ein: "EIN (US)", trn: "TRN (GCC)", crn: "Company reg. no.", other: "Other" },
      primary: "Primary",
      add: "Add legal entity",
      edit: "Edit",
      newLegalEntity: "New legal entity",
      editLegalEntity: "Edit legal entity",
      setPrimary: "Make primary",
      addTaxId: "Add tax ID",
      remove: "Remove",
      addBranch: "Add branch",
      newBranch: "New branch",
      editBranch: "Edit branch",
      noLegalEntities: "No legal entities yet. The first one becomes the primary entity of the company.",
      noBranches: "No branches yet.",
      noLegalEntity: "—",
      warehouseCount: "{{count}} warehouse(s)",
      errors: ERRORS_EN,
      scopes: {
        user: "User",
        access: "Access",
        unrestricted: "All legal entities",
        restricted: "{{count}} legal entities",
        allLegalEntities: "Access to all legal entities",
        legalEntities: "Legal entities",
        branches: "Branches (none selected = all)",
        warehouses: "Warehouses (none selected = all)",
        editTitle: "Access of {{name}}",
        reset: "Remove restriction",
        noUsers: "There are no users linked to the company.",
        hint: "Restricted users only see and book records of the selected legal entities, branches and warehouses. Server-side enforcement is switched on after the client release."
      },
      quota: {
        title: "Group quota",
        subtitle: "Totals across all legal entities; every active license adds its capacity.",
        users: "Users",
        operations: "Operations",
        storage: "Storage",
        licenses: "Active licenses: {{count}}",
        noLicense: "No active license — default capacity of one license.",
        perLicense: "Per license",
        breakdown: "By legal entity",
        entity: "Legal entity",
        unassigned: "Not assigned (before legal entities)"
      }
    },
    warehouseOrg: { legalEntity: "Legal entity", branch: "Branch", none: "—", edit: "Edit warehouse" }
  },
  tr: {
    nav: { organization: "Organizasyon" },
    org: {
      title: "Organizasyon",
      subtitle: "Tüzel kişilikler, şubeler ve kimin hangisinde çalışabileceği.",
      loadError: "Organizasyon yüklenemedi.",
      saveError: "Kaydedilemedi.",
      saved: "Kaydedildi.",
      readOnly: "Organizasyonu yalnızca şirket hesabı değiştirebilir.",
      tabs: { legalEntities: "Tüzel kişilikler", branches: "Şubeler", scopes: "Kullanıcı erişimi" },
      active: {
        label: "Çalışılan tüzel kişilik",
        hint: "Yeni kayıtlar bu tüzel kişiliğe yazılır."
      },
      fields: {
        code: "Kod",
        name: "Ad",
        country: "Ülke (ISO)",
        currency: "Fonksiyonel para birimi",
        fiscalYearStart: "Mali yıl başlangıcı",
        fiscalYear: "Cari mali yıl",
        taxIds: "Vergi kimlikleri",
        taxType: "Tür",
        taxValue: "Numara",
        taxCountry: "Ülke",
        legalAddress: "Yasal adres",
        line1: "Sokak / cadde",
        line2: "Adres satırı 2",
        city: "Şehir",
        region: "Bölge / eyalet",
        postalCode: "Posta kodu",
        language: "Dil",
        timezone: "Saat dilimi",
        status: "Durum",
        legalEntity: "Tüzel kişilik",
        branch: "Şube",
        warehouses: "Depolar"
      },
      statuses: { active: "Aktif", inactive: "Pasif" },
      taxTypes: { vkn: "VKN (TR)", tckn: "TCKN (TR)", vat: "KDV / VAT", ein: "EIN (ABD)", trn: "TRN (Körfez)", crn: "Ticaret sicil no", other: "Diğer" },
      primary: "Birincil",
      add: "Tüzel kişilik ekle",
      edit: "Düzenle",
      newLegalEntity: "Yeni tüzel kişilik",
      editLegalEntity: "Tüzel kişiliği düzenle",
      setPrimary: "Birincil yap",
      addTaxId: "Vergi kimliği ekle",
      remove: "Kaldır",
      addBranch: "Şube ekle",
      newBranch: "Yeni şube",
      editBranch: "Şubeyi düzenle",
      noLegalEntities: "Henüz tüzel kişilik yok. İlk eklenen şirketin birincil tüzel kişiliği olur.",
      noBranches: "Henüz şube yok.",
      noLegalEntity: "—",
      warehouseCount: "{{count}} depo",
      errors: ERRORS_TR,
      scopes: {
        user: "Kullanıcı",
        access: "Erişim",
        unrestricted: "Tüm tüzel kişilikler",
        restricted: "{{count}} tüzel kişilik",
        allLegalEntities: "Tüm tüzel kişiliklere erişim",
        legalEntities: "Tüzel kişilikler",
        branches: "Şubeler (seçim yoksa tümü)",
        warehouses: "Depolar (seçim yoksa tümü)",
        editTitle: "{{name}} erişimi",
        reset: "Kısıtı kaldır",
        noUsers: "Şirkete bağlı kullanıcı yok.",
        hint: "Kısıtlı kullanıcılar yalnızca seçilen tüzel kişilik, şube ve depoların kayıtlarını görür ve bunlara kayıt açar. Sunucu tarafı denetim istemci yayınından sonra açılır."
      },
      quota: {
        title: "Grup kotası",
        subtitle: "Tüm tüzel kişiliklerin toplamı; her aktif lisans kapasitesini ekler.",
        users: "Kullanıcı",
        operations: "İşlem",
        storage: "Depolama",
        licenses: "Aktif lisans: {{count}}",
        noLicense: "Aktif lisans yok — tek lisanslık varsayılan kapasite.",
        perLicense: "Lisans başına",
        breakdown: "Tüzel kişilik kırılımı",
        entity: "Tüzel kişilik",
        unassigned: "Atanmamış (tüzel kişilik öncesi)"
      }
    },
    warehouseOrg: { legalEntity: "Tüzel kişilik", branch: "Şube", none: "—", edit: "Depoyu düzenle" }
  },
  ar: {
    nav: { organization: "المؤسسة" },
    org: {
      title: "المؤسسة",
      subtitle: "الكيانات القانونية والفروع ومن يمكنه العمل في كل منها.",
      loadError: "تعذر تحميل المؤسسة.",
      saveError: "تعذر الحفظ.",
      saved: "تم الحفظ.",
      readOnly: "حساب الشركة فقط يمكنه تعديل المؤسسة.",
      tabs: { legalEntities: "الكيانات القانونية", branches: "الفروع", scopes: "صلاحيات المستخدمين" },
      active: {
        label: "الكيان الحالي",
        hint: "تُسجل السجلات الجديدة باسم هذا الكيان القانوني."
      },
      fields: {
        code: "الرمز",
        name: "الاسم",
        country: "الدولة (ISO)",
        currency: "العملة الوظيفية",
        fiscalYearStart: "بداية السنة المالية",
        fiscalYear: "السنة المالية الحالية",
        taxIds: "الأرقام الضريبية",
        taxType: "النوع",
        taxValue: "الرقم",
        taxCountry: "الدولة",
        legalAddress: "العنوان القانوني",
        line1: "الشارع",
        line2: "سطر العنوان 2",
        city: "المدينة",
        region: "المنطقة / الولاية",
        postalCode: "الرمز البريدي",
        language: "اللغة",
        timezone: "المنطقة الزمنية",
        status: "الحالة",
        legalEntity: "الكيان القانوني",
        branch: "الفرع",
        warehouses: "المستودعات"
      },
      statuses: { active: "نشط", inactive: "غير نشط" },
      taxTypes: { vkn: "VKN (تركيا)", tckn: "TCKN (تركيا)", vat: "ضريبة القيمة المضافة", ein: "EIN (أمريكا)", trn: "TRN (الخليج)", crn: "رقم السجل التجاري", other: "أخرى" },
      primary: "رئيسي",
      add: "إضافة كيان قانوني",
      edit: "تعديل",
      newLegalEntity: "كيان قانوني جديد",
      editLegalEntity: "تعديل الكيان القانوني",
      setPrimary: "تعيين كرئيسي",
      addTaxId: "إضافة رقم ضريبي",
      remove: "إزالة",
      addBranch: "إضافة فرع",
      newBranch: "فرع جديد",
      editBranch: "تعديل الفرع",
      noLegalEntities: "لا توجد كيانات قانونية بعد. أول كيان يُضاف يصبح الكيان الرئيسي للشركة.",
      noBranches: "لا توجد فروع بعد.",
      noLegalEntity: "—",
      warehouseCount: "{{count}} مستودع",
      errors: ERRORS_AR,
      scopes: {
        user: "المستخدم",
        access: "الصلاحية",
        unrestricted: "كل الكيانات القانونية",
        restricted: "{{count}} كيانات قانونية",
        allLegalEntities: "الوصول إلى كل الكيانات القانونية",
        legalEntities: "الكيانات القانونية",
        branches: "الفروع (بدون تحديد = الكل)",
        warehouses: "المستودعات (بدون تحديد = الكل)",
        editTitle: "صلاحيات {{name}}",
        reset: "إزالة التقييد",
        noUsers: "لا يوجد مستخدمون مرتبطون بالشركة.",
        hint: "المستخدمون المقيدون يرون ويسجلون فقط سجلات الكيانات والفروع والمستودعات المحددة. يُفعّل التحقق من جهة الخادم بعد إصدار التطبيقات."
      },
      quota: {
        title: "حصة المجموعة",
        subtitle: "إجماليات عبر كل الكيانات القانونية؛ كل ترخيص نشط يضيف سعته.",
        users: "المستخدمون",
        operations: "العمليات",
        storage: "التخزين",
        licenses: "التراخيص النشطة: {{count}}",
        noLicense: "لا يوجد ترخيص نشط — سعة افتراضية لترخيص واحد.",
        perLicense: "لكل ترخيص",
        breakdown: "حسب الكيان القانوني",
        entity: "الكيان القانوني",
        unassigned: "غير مخصص (قبل الكيانات القانونية)"
      }
    },
    warehouseOrg: { legalEntity: "الكيان القانوني", branch: "الفرع", none: "—", edit: "تعديل المستودع" }
  }
};
